import { Box, Slider } from '@mui/material';
import { FC, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Programme } from '../../../services/type';

type Props = {
    programme: Programme;
};

const ParticipantFilter: FC<Props> = ({ programme }) => {
    const DEBOUNCE_DELAY = 700;
    const [searchParam, setSearchParams] = useSearchParams();
    const debounceRef = useRef<number | null>(null);

    const participantOptions = Array.from(
        new Set(
            programme.reunions.flatMap((reunion) =>
                reunion.courses.map((course) => course.nombreDeclaresPartants)
            )
        )
    )
        .sort((a, b) => a - b)
        .map((p) => {
            return { value: p, label: p };
        });

    const minParticipants = participantOptions[0]?.value;
    const minParticipantsInput = searchParam.get("minParticipant") || participantOptions[0]?.value;
    const maxParticipants = participantOptions[participantOptions.length - 1]?.value;
    const maxParticipantsInput = searchParam.get("maxParticipant") || participantOptions[participantOptions.length - 1]?.value;

    const handleSliderChange = (_: Event, value: number | number[]) => {
        if (!Array.isArray(value)) return;

        // debounce filter update
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = window.setTimeout(() => {
            const params = new URLSearchParams(searchParam);

            const [min, max] = value;

            if (
                String(min) === String(minParticipants) &&
                String(max) === String(maxParticipants)
            ) {
                params.delete('minParticipant');
                params.delete('maxParticipant');
            } else {
                params.set('minParticipant', String(min));
                params.set('maxParticipant', String(max));
            }

            setSearchParams(params, { replace: true });
        }, DEBOUNCE_DELAY);
    };


    function valuetext(value: number) {
        return `${value} participants`;
    }

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <Box width={'80%'}>
            <Slider
                aria-label="Restricted values"
                getAriaValueText={valuetext}
                sx={{
                    color: "primary.light",
                    '& .MuiSlider-markLabel': {
                        color: 'white',
                    },
                }}
                defaultValue={[Number(minParticipantsInput), Number(maxParticipantsInput)]}
                onChange={handleSliderChange}
                step={null}
                valueLabelDisplay="auto"
                max={participantOptions.at(-1)?.value}
                min={participantOptions[0]?.value}
                marks={participantOptions}
            />
        </Box>
    );
};

export default ParticipantFilter;
